import { Point } from "@/common/types/point";
import { request } from "@/common/api";
import { Buttons } from "@/components/atoms/Buttons";
import { Loading } from "@/components/atoms/Loading";
import { TabList } from "@/components/molecules/TabList";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertText } from "@/components/atoms/AlertText";
import { TabContent } from "@/components/organisms/TabContent";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { PointContent } from "@/components/organisms/PointContent";
import { useRecoilValue } from "recoil";
import { PointHistoryList } from "@/components/molecules/PointHistoryList";
import { GET_EXERCISE_INFO, GET_POINT_HISTORY } from "@/common/querykeys";
import { PointStandardContent } from "@/components/organisms/PointStandardContent";
import { ReactComponent as IconArrow } from "@/styles/assets/svg/icon_arrow.svg";
import {
  guardianUserState,
  loginTypeState,
  studentUserState,
} from "@/common/states/user";
import { ExerciseInfo } from "@/common/types/healthData";

export const PointPage = () => {
  /** 📦 State Store */
  const student = useRecoilValue(studentUserState);
  const guardian = useRecoilValue(guardianUserState);
  const loginType = useRecoilValue(loginTypeState);
  const studentName =
    loginType === "student" ? student?.name : guardian?.student?.name;
  const [activeTab, setActiveTab] = useState("history");

  /** 🧺 Variables Defines */
  let etcCount = 0;
  let attendanceCount = 0;
  let weekExerciseCount = 0;

  /** 📡 API Layer */
  const { data: points, isPending } = useQuery<{
    totalPoint: number;
    pointHistory: Point[];
  }>({
    queryKey: [GET_POINT_HISTORY],
    queryFn: async () => {
      const studentId =
        loginType === "student" ? student?.id : guardian?.student?.id;

      return await request<{
        totalPoint: number;
        pointHistory: Point[];
      }>({
        method: "GET",
        url: `/point/history/${studentId}`,
      });
    },
  });

  const { data: exerciseInfo } = useQuery<ExerciseInfo>({
    queryKey: [GET_EXERCISE_INFO],
    queryFn: async () => {
      return await request<ExerciseInfo>({
        method: "GET",
        url: `/exercise/exercise-info/${student.id}`,
      });
    },
  });

  /** ⚙️ Data Processing Logic */
  if (points?.pointHistory) {
    etcCount = 0;
    attendanceCount = 0;
    weekExerciseCount = 0;

    points.pointHistory.forEach((point) => {
      if (point.reason === "출석 체크 완료") {
        attendanceCount++;
      } else if (point.reason === "나의 운동 프로그램 참여 완료") {
        weekExerciseCount++;
      } else {
        etcCount++;
      }
    });
  }

  return (
    <MainTemplate
      nav={false}
      subPage={false}
      menuBtn={false}
      pageTitle="건강 포인트"
      buttonLeft={
        <Buttons
          type="button"
          size="headerTextIcon"
          layout="text"
          color="bright100"
          icon={<IconArrow />}
          label="뒤로"
          onClick={() => window.history.back()}
        />
      }
    >
      {isPending ? (
        <Loading />
      ) : (
        <>
          <TabContent
            nav={false}
            tabList={
              <TabList
                data={[
                  {
                    id: "history",
                    title: "지급내역",
                  },
                  {
                    id: "standard",
                    title: "포인트 지급기준",
                  },
                ]}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            }
          >
            {activeTab === "history" ? (
              <>
                {points?.pointHistory && points?.pointHistory.length > 0 ? (
                  <PointContent
                    userData={{
                      userName: studentName,
                      currentPoint: points?.totalPoint ?? 0,
                      attend_day: attendanceCount,
                      exercise_day: exerciseInfo?.exerciseComplete,
                      etc: etcCount,
                    }}
                  >
                    <PointHistoryList data={points?.pointHistory ?? []} />
                  </PointContent>
                ) : (
                  <PointContent
                    userData={{
                      userName: studentName,
                      currentPoint: points?.totalPoint ?? 0,
                      attend_day: attendanceCount,
                      exercise_day: exerciseInfo?.exerciseComplete,
                      etc: etcCount,
                    }}
                  >
                    <AlertText
                      mainText="획득한 건강 포인트가 없습니다."
                      subText={
                        <>
                          운동을 시작하고 건강도 챙기고, 포인트도 쌓아보세요!
                          <br />
                          꾸준한 활동이 큰 보상을 가져옵니다.
                        </>
                      }
                    />
                  </PointContent>
                )}
              </>
            ) : (
              <PointStandardContent />
            )}
          </TabContent>
        </>
      )}
    </MainTemplate>
  );
};
